import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { toYmdLocal } from "@/lib/utils";

export interface ZipNormalizationResult {
  original: string;
  normalized: string;
  isValid: boolean;
  issues: string[];
}

export interface GeoResolutionResult {
  zipCode: string;
  state: string;
  ratingAreaId: string | null;
  ratingAreaName: string | null;
  county: string | null;
  countyFips: string | null;
  /** "zip" | "zip_prefix" | "county" when resolved. */
  matchType?: string | null;
  isExactMatch: boolean;
  /** Why it didn't resolve (e.g. the county is needed), when unresolved. */
  warnings: string[];
}

export interface ValidationIssue {
  field: string;
  code: string;
  message: string;
  severity: "error" | "warning";
}

export interface QuoteValidationResult {
  isValid: boolean;
  effectiveDate: string;
  groupState: string;
  groupZip: string;
  normalizedZip: string;
  ratingAreaId: string | null;
  ratingAreaName: string | null;
  issues: ValidationIssue[];
  warnings: string[];
  employeeCount: number;
  eligibleEmployeeCount: number;
}

export interface PlanEligibilityResult {
  eligiblePlans: PlanInfo[];
  ineligiblePlans: { plan: PlanInfo; reasons: string[] }[];
  totalPlansChecked: number;
  eligibleCount: number;
  ineligibleCount: number;
}

export interface PlanInfo {
  id: number | string;
  carrierId?: number;
  carrierName?: string;
  planName?: string;
  sicExclusions?: string[];
  sicInclusions?: string[];
  minGroupSize?: number;
  maxGroupSize?: number;
  serviceAreaZips?: string[];
  serviceAreaStates?: string[];
  effectiveStartDate?: string;
  effectiveEndDate?: string;
}

export interface EligibilityContext {
  groupState: string;
  groupZip: string;
  groupSic: string;
  groupSize: number;
  effectiveDate: Date | string;
  ratingAreaId?: string;
}

export function useNormalizeZip(zip: string | undefined) {
  return useQuery<ZipNormalizationResult>({
    queryKey: ["/api/quote-validation/normalize-zip", zip],
    queryFn: async () => {
      if (!zip) throw new Error("ZIP code required");
      const res = await fetch(`/api/quote-validation/normalize-zip/${zip}`);
      if (!res.ok) throw new Error("Failed to normalize ZIP");
      return res.json();
    },
    enabled: !!zip && zip.length >= 3,
    staleTime: Infinity,
  });
}

export function useResolveGeo(
  state: string | undefined,
  zip: string | undefined,
  effectiveDate?: Date,
  county?: string,
) {
  return useQuery<GeoResolutionResult>({
    queryKey: ["/api/quote-validation/resolve-geo", state, zip, toYmdLocal(effectiveDate), county],
    queryFn: async () => {
      if (!state || !zip) throw new Error("State and ZIP required");
      const params = new URLSearchParams();
      const effectiveYmd = toYmdLocal(effectiveDate);
      if (effectiveYmd) params.set("effectiveDate", effectiveYmd);
      // County lets county-based states resolve when the ZIP has no mapping.
      if (county) params.set("county", county);
      const qs = params.toString();
      const url = `/api/quote-validation/resolve-geo/${state}/${zip}${qs ? `?${qs}` : ""}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to resolve geo context");
      return res.json();
    },
    enabled: !!state && !!zip && zip.length >= 5,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useValidateQuote() {
  return useMutation<QuoteValidationResult, Error, {
    effectiveDate: Date | string;
    groupState: string;
    groupZip: string;
    groupSic?: string;
    employees?: Array<{
      id: number;
      dateOfBirth?: string;
      status?: string;
      zipCode?: string;
    }>;
    clientId?: number;
    prospectId?: number;
  }>({
    mutationFn: async (payload) => {
      const res = await apiRequest("POST", "/api/quote-validation/validate", payload);
      return res.json();
    },
  });
}

export function useFilterPlans() {
  return useMutation<PlanEligibilityResult, Error, {
    plans: PlanInfo[];
    context: EligibilityContext;
    clientId?: number;
    prospectId?: number;
  }>({
    mutationFn: async (payload) => {
      const res = await apiRequest("POST", "/api/quote-validation/filter-plans", payload);
      return res.json();
    },
  });
}

export function useQuoteValidationAuditLogs(params: { 
  quoteId?: number; 
  clientId?: number; 
  prospectId?: number;
  limit?: number;
}) {
  const { quoteId, clientId, prospectId, limit = 50 } = params;
  
  return useQuery({
    queryKey: ["/api/quote-validation/audit-logs", quoteId, clientId, prospectId, limit],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (quoteId) searchParams.set("quoteId", String(quoteId));
      if (clientId) searchParams.set("clientId", String(clientId));
      if (prospectId) searchParams.set("prospectId", String(prospectId));
      if (limit) searchParams.set("limit", String(limit));
      
      const res = await fetch(`/api/quote-validation/audit-logs?${searchParams}`);
      if (!res.ok) throw new Error("Failed to fetch audit logs");
      return res.json();
    },
    enabled: !!(quoteId || clientId || prospectId),
  });
}
